import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/App';
import { 
  MessageCircle, 
  ArrowLeft, 
  Package, 
  Clock,
  ChevronRight
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function MessagesPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchConversations();
  }, []);

  const fetchConversations = async () => {
    try {
      // Get all user's bids (as bidder)
      const myBidsRes = await axios.get(`${API_URL}/api/bids/my-bids`);
      
      // Get all bids on user's listings (as seller)
      const myListingsRes = await axios.get(`${API_URL}/api/listings/user/${user.id}`);
      
      const allConversations = [];

      // Add bids where user is bidder
      for (const bid of myBidsRes.data) {
        try {
          const listing = await axios.get(`${API_URL}/api/listings/${bid.listing_id}`);
          const seller = await axios.get(`${API_URL}/api/users/${listing.data.user_id}`);
          const messages = await axios.get(`${API_URL}/api/messages/bid/${bid.id}`);
          
          allConversations.push({
            bidId: bid.id,
            listing: listing.data,
            otherUser: seller.data,
            bid: bid,
            lastMessage: messages.data[messages.data.length - 1] || null,
            messageCount: messages.data.length,
            role: 'bidder'
          });
        } catch (err) {
          // Skip if listing/user not found
          console.debug('Skipping conversation (bidder):', err?.response?.status);
        }
      }

      // Add bids on user's listings (as seller)
      for (const listing of myListingsRes.data) {
        try {
          const bidsRes = await axios.get(`${API_URL}/api/bids/listing/${listing.id}`);
          for (const bid of bidsRes.data) {
            const bidder = await axios.get(`${API_URL}/api/users/${bid.bidder_id}`);
            const messages = await axios.get(`${API_URL}/api/messages/bid/${bid.id}`);
            
            allConversations.push({
              bidId: bid.id,
              listing: listing,
              otherUser: bidder.data,
              bid: bid,
              lastMessage: messages.data[messages.data.length - 1] || null,
              messageCount: messages.data.length,
              role: 'seller'
            });
          }
        } catch (err) {
          // Skip errors
          console.debug('Skipping conversation (seller):', err?.response?.status);
        }
      }

      // Sort by last message time (most recent first)
      allConversations.sort((a, b) => {
        const timeA = a.lastMessage ? new Date(a.lastMessage.created_at) : new Date(a.bid.created_at);
        const timeB = b.lastMessage ? new Date(b.lastMessage.created_at) : new Date(b.bid.created_at);
        return timeB - timeA;
      });

      // Remove duplicates (same bid from both perspectives)
      const uniqueConversations = allConversations.filter((conv, index, self) =>
        index === self.findIndex(c => c.bidId === conv.bidId)
      );

      setConversations(uniqueConversations);
    } catch (error) {
      console.error('Failed to fetch conversations', error);
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffDays === 0) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return date.toLocaleDateString([], { weekday: 'short' });
    }
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  const getStatusColor = (status) => {
    const colors = {
      submitted: 'bg-blue-500',
      countered: 'bg-amber-500',
      accepted: 'bg-emerald-500',
      rejected: 'bg-red-500',
      withdrawn: 'bg-slate-400',
      not_selected: 'bg-slate-400'
    };
    return colors[status] || 'bg-slate-400';
  };

  return (
    <div className="min-h-screen bg-slate-50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-slate-600 hover:text-indigo-600 mb-6 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          <span className="font-medium">Back to Home</span>
        </Link>

        <div className="mb-8">
          <h1 className="text-3xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Messages
          </h1>
          <p className="text-slate-600">Your conversations about bids and trades</p>
        </div>

        {loading ? (
          <div className="text-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          </div>
        ) : conversations.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-2xl border border-slate-200">
            <MessageCircle className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-slate-900 mb-2">No conversations yet</h3>
            <p className="text-slate-600 mb-6">Start bidding or receive bids to begin conversations</p>
            <Link
              to="/"
              className="inline-block rounded-full font-bold px-8 py-3 bg-indigo-600 text-white hover:bg-indigo-700 transition-all"
            >
              Browse Listings
            </Link>
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden divide-y divide-slate-100">
            {conversations.map((conv) => (
              <Link
                key={conv.bidId}
                to={`/chat/${conv.bidId}`}
                className="flex items-center gap-4 p-4 hover:bg-slate-50 transition-colors"
                data-testid={`conversation-${conv.bidId}`}
              >
                {/* User Avatar */}
                <div className="relative flex-shrink-0">
                  <div className="w-14 h-14 bg-indigo-500 rounded-full flex items-center justify-center">
                    <span className="text-white text-lg font-bold">
                      {conv.otherUser?.name?.[0]?.toUpperCase() || '?'}
                    </span>
                  </div>
                  <div className={`absolute -bottom-1 -right-1 w-4 h-4 ${getStatusColor(conv.bid.status)} rounded-full border-2 border-white`} />
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    <p className="font-semibold text-slate-900 truncate">
                      {conv.otherUser?.name}
                    </p>
                    <span className="text-xs text-slate-400 flex-shrink-0 ml-2">
                      {formatTime(conv.lastMessage?.created_at || conv.bid.created_at)}
                    </span>
                  </div>
                  
                  <div className="flex items-center gap-2 mb-1">
                    <Package className="w-4 h-4 text-slate-400 flex-shrink-0" />
                    <p className="text-sm text-slate-600 truncate">{conv.listing.title}</p>
                  </div>

                  <p className="text-sm text-slate-500 truncate">
                    {conv.lastMessage 
                      ? (conv.lastMessage.sender_id === user.id ? 'You: ' : '') + conv.lastMessage.content
                      : conv.role === 'seller' 
                        ? `New bid received`
                        : `Bid placed`
                    }
                  </p>
                </div>

                {/* Arrow */}
                <ChevronRight className="w-5 h-5 text-slate-400 flex-shrink-0" />
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
